import moment from 'moment';
import * as React from 'react';
import { createPortal } from 'react-dom';

import RemoveActivityModal from './remove_activity_modal';

import { requestPut } from '../../../../../modules/request/index';

import useSnackbarMonitor from '../../../../Shared/hooks/useSnackbarMonitor';
import { DataTableChip, ShrinkWrapChip, Snackbar, Tooltip, defaultSnackbarTimeout, safeParseHtml, safeStripHtml, EXTRA_SMALL_ICON_BASE_SRC } from '../../../../Shared/index';
import DemoGuardedDataTable from '../../shared/demoGuardedDataTable';
import DemoGuardedDateTimePickerInput from '../../shared/demoGuardedDateTimePickerInput';
import { getExtraSmallIconForActivityClassification } from '../../../../Shared/libs';
import {
  DUE_DATE_DEFAULT_TEXT,
  INVALID_DATES_SNACKBAR_COPY,
  PUBLISH_DATE_DEFAULT_TEXT,
  formatDateTimeForDisplay,
  CopyToAllButton,
} from '../../../helpers/unitActivityDates';
import * as api from '../../modules/call_api';
import { useDemoAccountContextProvider } from '../../../contexts/DemoAccountContextProvider';

const shareActivitySrc = `${window.postbuildEnv.CDN_URL}/images/icons/icons-share.svg`

const publishedIconImage = <img alt="" className="published-icon" src={`${EXTRA_SMALL_ICON_BASE_SRC}/check-small.svg`} />
const scheduledIconImage = <img alt="" className="scheduled-icon" src={`${EXTRA_SMALL_ICON_BASE_SRC}/clock-check.svg`} />

export const AVERAGE_FONT_WIDTH = 7

const PUBLISH_DATE_ATTRIBUTE_KEY = 'publish_date'
const DUE_DATE_ATTRIBUTE_KEY = 'due_date'

const STAGGERED_RELEASE_DATE_TEXT = "This activity is set for staggered release. This means that it will be unlocked when a student completes the previous activity pack in the sequence. As a result, you cannot set the publish date or due date."

const tableHeaders = (isOwner) => ([
  {
    name: 'Tool',
    attribute: 'tool',
    width: isOwner ? '60px' : '50px',
    headerClassName: isOwner ? 'center-content' : null,
    rowSectionClassName: 'center-content',
    noTooltip: true
  },
  {
    name: 'Activity',
    attribute: 'activity',
    width: isOwner ? '502px' : '652px',
    headerClassName: 'grow-to-fit',
    rowSectionClassName: 'grow-to-fit',
    renderCellsAsHeaders: true
  },
  {
    ariaLabel: 'Publish date',
    name: (
      <Tooltip
        tooltipText="When you schedule an activity, it is displayed in your dashboard, but it is hidden from a student until the publish date."
        tooltipTriggerText={<span className="publish-date-header">Publish date</span>}
      />
    ),
    attribute: 'publishDatePicker',
    width: '170px',
    headerClassName: 'publish-date-header-container',
    rowSectionClassName: 'datetime-picker'
  },
  {
    ariaLabel: 'Due date',
    name: (
      <Tooltip
        tooltipText="Display a due date to students for this activity. Students can still access an activity after the due date."
        tooltipTriggerText={<span className="due-date-header">Due date <span>(optional)</span></span>}
      />
    ),
    attribute: 'dueDatePicker',
    width: '170px',
    headerClassName: isOwner ? 'due-date-header-container' : 'due-date-header-container no-right-margin',
    rowSectionClassName: isOwner ? 'datetime-picker' : 'datetime-picker no-right-margin'
  },
  {
    name: 'Share',
    attribute: 'shareActivity',
    width: '80px',
    headerClassName: 'center-content',
    rowSectionClassName: 'center-content share',
    noTooltip: true
  }
])

// portaled nodes lose their ancestor CSS scope; recreate `.my-activities > .activity-pack`
const wrapInPackScope = (children, extraClassName = '') => (
  <div className={`my-activities ${extraClassName}`.trim()}>
    <section className="activity-pack">{children}</section>
  </div>
)

// classes must mirror the DemoGuardedDataTable rendered below
const dragOverlayWrapper = (children) => wrapInPackScope(
  <table className="data-table is-owner reporting-format">
    <tbody className="data-table-body reorderable">{children}</tbody>
  </table>
)

const ActivityTable = ({ data, onSuccess, isOwner, handleActivityClicked, handleToggleModal, renderRemoveModalInPortal, onRemoveModalToggle, datePickersInPortal, dragOverlayInPortal }) => {
  const classroomActivityArray = Array.from(data.classroomActivities).map(ca => ca[1])
  const { isDemo } = useDemoAccountContextProvider()

  const [activityOrder, setActivityOrder] = React.useState(classroomActivityArray.map(ca => ca.activityId) || [])
  const [showRemoveActivityModal, setShowRemoveActivityModal] = React.useState(false)
  const [unitActivityIdToRemove, setUnitActivityIdToRemove] = React.useState(null)
  const [erroredUnitActivityIds, setErroredUnitActivityIds] = React.useState([])
  const [showSnackbar, setShowSnackbar] = React.useState(false)
  const [snackbarText, setSnackbarText] = React.useState('')

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  React.useEffect(() => {
    setErroredUnitActivityIds([])
  }, [data])

  const onRemoveModalToggleRef = React.useRef(onRemoveModalToggle)
  onRemoveModalToggleRef.current = onRemoveModalToggle
  function notifyRemoveModalToggle(open) {
    if (onRemoveModalToggleRef.current) { onRemoveModalToggleRef.current(open) }
  }
  React.useEffect(() => {
    return () => { notifyRemoveModalToggle(false) }
  }, [])

  function removeActivity(unitActivityId) {
    requestPut(`${window.postbuildEnv.DEFAULT_URL}/teachers/unit_activities/${unitActivityId}/hide`, {}, () => onSuccess('Activity removed'))
    setShowRemoveActivityModal(false)
    notifyRemoveModalToggle(false)
  }

  function closeDatePicker(date, unitActivityId, dateAttributeKey) {
    const activity = classroomActivityArray.find(act => act.uaId === unitActivityId)
    const activityHasDueDateBeforePublishDate = date && (dateAttributeKey === PUBLISH_DATE_ATTRIBUTE_KEY ?
      (activity.dueDate && date >= new Date(activity.dueDate)) :
      (activity.publishDate && date <= new Date(activity.publishDate)))
    if (activityHasDueDateBeforePublishDate) {
      setErroredUnitActivityIds([unitActivityId])
      setSnackbarText(INVALID_DATES_SNACKBAR_COPY)
      setShowSnackbar(true)
    } else {
      requestPut(`/teachers/unit_activities/${unitActivityId}`, { unit_activity: { [dateAttributeKey]: date, } }, () => onSuccess());
    }
  }

  function closeRemoveActivityModal() {
    setShowRemoveActivityModal(false)
    notifyRemoveModalToggle(false)
  }

  function openRemoveActivityModal(unitActivityId) {
    setUnitActivityIdToRemove(unitActivityId)
    setShowRemoveActivityModal(true)
    notifyRemoveModalToggle(true)
  }

  function copyPublishDateToAll() {
    const publishDate = classroomActivityArray[0].publishDate
    const activitiesWithEarlierDueDates = classroomActivityArray.filter(act => publishDate >= act.dueDate)
    if (publishDate && activitiesWithEarlierDueDates.length) {
      setErroredUnitActivityIds(activitiesWithEarlierDueDates.map(a => a.uaId))
      setSnackbarText(INVALID_DATES_SNACKBAR_COPY)
      setShowSnackbar(true)
    } else {
      copyDateToAll(publishDate, PUBLISH_DATE_ATTRIBUTE_KEY)
    }
  }

  function copyDueDateToAll() {
    const dueDate = classroomActivityArray[0].dueDate
    const activitiesWithEarlierDueDates = classroomActivityArray.filter(act => dueDate <= act.publishDate)
    if (dueDate && activitiesWithEarlierDueDates.length) {
      setErroredUnitActivityIds(activitiesWithEarlierDueDates.map(a => a.uaId))
      setSnackbarText(INVALID_DATES_SNACKBAR_COPY)
      setShowSnackbar(true)
    } else {
      copyDateToAll(dueDate, DUE_DATE_ATTRIBUTE_KEY)
    }
  }

  function copyDateToAll(date, dateAttributeKey) {
    const unitActivityIds = classroomActivityArray.map(ca => ca.uaId)
    requestPut('/teachers/unit_activities/update_multiple_dates', { unit_activity_ids: unitActivityIds, date: date, date_attribute: dateAttributeKey }, () => { onSuccess('Dates copied to all') });
  }

  function reorderCallback(sortInfo) {
    const newUnitActivityOrder = sortInfo.map(item => item.key).filter(Boolean);
    const newActivityOrder = newUnitActivityOrder.map(unitActivityId => classroomActivityArray.find(ca => String(ca.uaId) === String(unitActivityId)).activityId)
    setActivityOrder(newActivityOrder)
    api.changeActivityPackOrder(data.unitId, newActivityOrder, () => onSuccess(null), null)
  }

  function handleShareActivityClick(activity) {
    handleActivityClicked(activity)
    handleToggleModal()
  }

  function getActivityNameToRemove() {
    return safeStripHtml(classroomActivityArray.find(act => act.uaId === unitActivityIdToRemove)?.name)
  }

  const activityRows = activityOrder.flatMap((activityId, i) => {
    const activity = classroomActivityArray.find(act => act.activityId === activityId)
    if (!activity){ return }
    const previewLink = (
      <ShrinkWrapChip>
        <DataTableChip
          color="green"
          label={safeParseHtml(activity.name)}
          link={`/activity_sessions/anonymous?activity_id=${activity.activityId}`}
          openInNewTab={true}
        />
      </ShrinkWrapChip>
    )
    activity.activity = previewLink
    activity.tool = getExtraSmallIconForActivityClassification(activity.activityClassificationId)

    const dueDateAsDate = activity.dueDate ? new Date(activity.dueDate) : null
    const publishDateAsDate = activity.publishDate ? new Date(activity.publishDate) : null

    let activityDueDatePicker = dueDateAsDate ? formatDateTimeForDisplay(moment(dueDateAsDate)) : DUE_DATE_DEFAULT_TEXT
    let activityPublishDatePicker = publishDateAsDate ? formatDateTimeForDisplay(moment(publishDateAsDate)) : PUBLISH_DATE_DEFAULT_TEXT

    if (activity.staggered) {
      activityDueDatePicker = (
        <Tooltip
          tooltipText={STAGGERED_RELEASE_DATE_TEXT}
          tooltipTriggerText={activityDueDatePicker}
        />
      )

      activityPublishDatePicker = (
        <Tooltip
          tooltipText={STAGGERED_RELEASE_DATE_TEXT}
          tooltipTriggerText={activityPublishDatePicker}
        />
      )
    } else if (isOwner) {
      activityDueDatePicker = (
        <DemoGuardedDateTimePickerInput
          closeFunction={(date) => closeDatePicker(date, activity.uaId, DUE_DATE_ATTRIBUTE_KEY)}
          defaultText={DUE_DATE_DEFAULT_TEXT}
          disablePortal={!datePickersInPortal}
          initialValue={dueDateAsDate}
          keyForElement={`dueDate-${activity.uaId}`}
        />
      )

      activityPublishDatePicker = (
        <DemoGuardedDateTimePickerInput
          closeFunction={(date) => closeDatePicker(date, activity.uaId, PUBLISH_DATE_ATTRIBUTE_KEY)}
          defaultText={PUBLISH_DATE_DEFAULT_TEXT}
          disablePortal={!datePickersInPortal}
          icon={activity.scheduled ? scheduledIconImage : publishedIconImage}
          initialValue={publishDateAsDate}
          keyForElement={`publishDate-${activity.uaId}`}
        />
      )
    }

    activity.dueDatePicker = activityDueDatePicker
    activity.publishDatePicker = activityPublishDatePicker

    activity.shareActivity = (
      <button className="share-activity-button focus-on-light" onClick={() => handleShareActivityClick(activity)} type="button" value="row-button">
        <img alt='share-arrow' src={shareActivitySrc} />
      </button>
    )
    activity.removable = true
    activity.id = activity.uaId
    activity.className = erroredUnitActivityIds.includes(activity.uaId) ? 'checked' : ''

    // add a separate row for the "Copy to All" buttons below the first activity row
    if (i === 0 && activityOrder.length > 1 && !activity.staggered && isOwner) {
      return [
        activity,
        {
          id: '',
          className: '',
          tool: '',
          activity: '',
          concept: '',
          dueDatePicker: (<CopyToAllButton disabled={erroredUnitActivityIds.includes(activity.uaId)} handleClickCopyToAll={copyDueDateToAll} isDemoProtected={true} />),
          publishDatePicker: (<CopyToAllButton handleClickCopyToAll={copyPublishDateToAll} isDemoProtected={true} />),
          removable: false
        },
      ];
    }

    return [activity];
  }).filter(Boolean)

  let removeActivityModalEl = null
  if (showRemoveActivityModal) {
    const modal = (
      <RemoveActivityModal
        activityName={getActivityNameToRemove()}
        closeFunction={closeRemoveActivityModal}
        removeFunction={() => removeActivity(unitActivityIdToRemove)}
      />
    )
    removeActivityModalEl = renderRemoveModalInPortal
      ? createPortal(wrapInPackScope(modal, 'remove-activity-modal-portal'), document.body)
      : modal
  }

  return (
    <React.Fragment>
      <Snackbar text={snackbarText} visible={showSnackbar} />
      {removeActivityModalEl}
      <DemoGuardedDataTable
        className={isOwner ? 'is-owner' : ''}
        headers={tableHeaders(isOwner)}
        isReorderable={isOwner}
        overlayInPortal={dragOverlayInPortal}
        overlayWrapper={dragOverlayInPortal ? dragOverlayWrapper : undefined}
        removeHeaderText="Remove"
        removeRow={openRemoveActivityModal}
        reorderCallback={reorderCallback}
        reportingFormat={true}
        rows={activityRows}
        showRemoveIcon={isOwner}
        sortIsDisabled={isDemo}
      />
    </React.Fragment>
  )
}

export default ActivityTable
